import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useBilling } from "../hooks/useBilling";
import { FaCreditCard, FaCheckCircle, FaClock, FaTimesCircle, FaSync, FaArrowLeft } from "react-icons/fa";
import toast from "react-hot-toast";

const PaymentHistory = () => {
  const navigate = useNavigate();
  const { fetchPaymentHistory } = useBilling();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadPayments = useCallback(async () => {
    setLoading(true);
    const result = await fetchPaymentHistory();
    if (result.success) {
      setPayments(result.data);
    } else {
      toast.error("Failed to load payment history");
    }
    setLoading(false);
  }, [fetchPaymentHistory]);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  const getTransactionStatusBadge = (status) => {
    const statusConfig = {
      COMPLETED: { color: "bg-green-100 text-green-800", icon: <FaCheckCircle className="mr-1" /> },
      FAILED: { color: "bg-red-100 text-red-800", icon: <FaTimesCircle className="mr-1" /> },
      PENDING: { color: "bg-yellow-100 text-yellow-800", icon: <FaClock className="mr-1" /> },
      PROCESSING: { color: "bg-blue-100 text-blue-800", icon: <FaClock className="mr-1" /> },
      REFUNDED: { color: "bg-purple-100 text-purple-800", icon: <FaSync className="mr-1" /> },
      CANCELLED: { color: "bg-gray-100 text-gray-800", icon: <FaTimesCircle className="mr-1" /> },
    };
    const config = statusConfig[status] || statusConfig.PENDING;
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}
      >
        {config.icon}
        {config.label || status}
      </span>
    );
  };

  const formatDateTime = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="text-center py-8 text-gray-600">Loading payment history...</div>
      );
    }
    if (payments.length === 0) {
      return (
        <div className="text-center py-8">
          <FaCreditCard className="text-gray-300 text-5xl mx-auto mb-3" />
          <p className="text-gray-500">No payment transactions yet</p>
        </div>
      );
    }
    return (
      <div className="space-y-4">
        {payments.map((payment) => (
          <div key={payment.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  {getTransactionStatusBadge(payment.status)}
                  <span className="text-lg font-semibold text-gray-800">
                    ${(parseFloat(payment.amount) || 0).toFixed(2)}
                  </span>
                  <span className="text-sm text-gray-500">
                    {formatDateTime(payment.initiatedAt)}
                  </span>
                </div>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>
                    <span className="font-medium">Bill Number:</span> {payment.billNumber}
                  </p>
                  <p>
                    <span className="font-medium">Gateway:</span> {payment.gateway}
                  </p>
                  {payment.paypalOrderId && (
                    <p>
                      <span className="font-medium">Order ID:</span> {payment.paypalOrderId}
                    </p>
                  )}
                  {payment.completedAt && (
                    <p>
                      <span className="font-medium">Completed:</span> {formatDateTime(payment.completedAt)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="p-6 bg-gray-50 min-h-full">
      <div className="mb-6">
        <button
          onClick={() => navigate("/manage-subscriptions")}
          className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 mb-4"
        >
          <FaArrowLeft />
          <span>Back to Billing</span>
        </button>
        <h2 className="text-2xl font-bold text-gray-800">Payment History</h2>
        <p className="text-gray-600 mt-1">View all your payment transactions</p>
      </div>

      <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
        <div className="p-6">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default PaymentHistory;